import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useIntl } from 'react-intl';

import {
  Level,
  useSnackNotificationContext,
} from '#src/contexts/snackNotification/index.ts';
import {
  type WalletUnlockFailureReason,
  useWalletContext,
} from '#src/contexts/wallet/index.ts';
import { type GraphQLError, formatGqlErrors } from '#src/lib/gql/index.ts';

import type {
  VerifyPhoneNumberMutation,
  VerifyPhoneNumberMutationVariables,
} from './__generated__/useVerifyPhoneNumber.graphql.ts';
import { walletUnlockMessages } from './walletUnlockMessages.ts';

export const PHONE_NUMBER_ALREADY_USED = 10_004;

/**
 * Code of the error returned whenever there is already a pending phone number verification
 * request for that phone number.
 */
const PHONE_NUMBER_PENDING_VERIFICATION = 10_006;

export type WalletUnlockFailure = 'locked' | 'noKey';

type WalletUnlockError = {
  message: string;
  walletUnlockFailure: WalletUnlockFailure;
};

export type VerifyPhoneNumberError = GraphQLError | WalletUnlockError;

const isWalletUnlockError = (
  error: VerifyPhoneNumberError
): error is WalletUnlockError => 'walletUnlockFailure' in error;

export const getWalletUnlockFailure = (
  errors: VerifyPhoneNumberError[]
): WalletUnlockFailure | null =>
  errors.find(isWalletUnlockError)?.walletUnlockFailure ?? null;

export const canProceedToVerificationCodeInput = (
  errors: { code?: number | string | null; message: string }[] | null
) => {
  return (
    !errors ||
    errors.length === 0 ||
    (errors.length === 1 &&
      errors[0].code === PHONE_NUMBER_PENDING_VERIFICATION)
  );
};

const VERIFY_PHONE_NUMBER_MUTATION = gql`
  mutation VerifyPhoneNumberMutation($input: verifyPhoneNumberInput!) {
    verifyPhoneNumber(input: $input) {
      currentUser {
        slug
        phoneNumber
        phoneNumberVerificationRequested
        unverifiedPhoneNumber
      }
      errors {
        code
        path
        message
      }
    }
  }
` as TypedDocumentNode<
  VerifyPhoneNumberMutation,
  VerifyPhoneNumberMutationVariables
>;

export const useVerifyPhoneNumber = () => {
  const [verifyPhoneNumber] = useMutation(VERIFY_PHONE_NUMBER_MUTATION);
  const { showNotification } = useSnackNotificationContext();
  const { loadWallet } = useWalletContext();
  const { formatMessage } = useIntl();

  const toWalletError = ({
    message,
    reason,
  }: {
    message: string;
    reason: WalletUnlockFailureReason;
  }): VerifyPhoneNumberError => {
    switch (reason) {
      case 'locked':
        return {
          message: formatMessage(walletUnlockMessages.locked),
          walletUnlockFailure: 'locked',
        };
      case 'noKey':
        return {
          message: formatMessage(walletUnlockMessages.noKey),
          walletUnlockFailure: 'noKey',
        };
      case 'keyFetchFailed':
        return { message: formatMessage(walletUnlockMessages.keyFetchFailed) };
      default:
        return { message };
    }
  };

  return async (
    phoneNumber: string
  ): Promise<VerifyPhoneNumberError[] | null> => {
    const loadResult = await loadWallet();
    if (loadResult.errors?.length) {
      return loadResult.errors.map(toWalletError);
    }
    const result = await verifyPhoneNumber({
      variables: {
        input: {
          phoneNumber,
        },
      },
    });

    const errors = result.data?.verifyPhoneNumber?.errors || [];

    if (errors.length) {
      showNotification(
        'errors',
        { errors: formatGqlErrors(errors) },
        { level: Level.WARN }
      );
      return errors;
    }
    return null;
  };
};
